/**
 * The API client. One place that knows the envelope, the token and the refresh.
 *
 * Four things it does, and each exists because the alternative went wrong
 * somewhere:
 *
 * **The access token is held in memory only** — never in `localStorage`, where
 * any script on the page can read it. So a cold page load has no token, and the
 * client knows it: an authorized request made without one refreshes from the
 * httpOnly cookie *first*, rather than sending a request certain to be refused.
 * (It used to send it: every page load and every new tab began with a 401 in
 * the console and a wasted round trip.) A refresh the server refuses means
 * nobody is signed in, and the request is not sent at all.
 *
 * **It unwraps the envelope**, so a caller gets `data` and never has to know the
 * wrapper exists.
 *
 * **It throws {@link ApiError} on the error envelope**, carrying the machine
 * code, the human message and the field details — so a form can highlight the
 * input that is wrong instead of showing a banner.
 *
 * **It performs a single-flight refresh and one retry on a 401.** Single-flight
 * matters: a page that fires six queries at once would otherwise send six
 * refreshes, and because refresh *rotates* the token, five of them would be
 * replaying an already-spent token and would log the user out.
 */

import { apiBaseUrl, appConfig, isConfigured } from '../config/app-config';

/** The success envelope every endpoint returns. */
interface SuccessEnvelope<T> {
  success: true;
  data: T;
  meta: {
    request_id: string;
    timestamp: string;
    pagination?: { page: number; page_size: number; total: number } | { next_cursor: string | null; limit: number; total: number | null };
  };
}

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string; details: ApiErrorDetail[] };
  meta: { request_id: string; timestamp: string };
}

export interface ApiErrorDetail {
  field: string;
  code: string;
  message: string;
}

/** One page of a server-side list, plus its total. */
export interface OffsetPage<T> {
  rows: T[];
  total: number;
}

/**
 * A failed API call.
 *
 * `code` is the stable contract to branch on. `message` is already written for
 * a person — the API is responsible for that, so a client showing it verbatim is
 * correct rather than lazy.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiErrorDetail[];
  readonly requestId: string;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: ApiErrorDetail[];
    requestId?: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? [];
    this.requestId = init.requestId ?? '';
  }

  /** Field-level messages keyed by field, for a form's error map. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details) {
      if (d.field) out[d.field] = d.message;
    }
    return out;
  }

  /** Whether this is the API saying the installation lacks the service. */
  get isServiceDisabled(): boolean {
    return this.code === 'SERVICE_NOT_ENABLED';
  }

  get isForbidden(): boolean {
    return this.code === 'PERMISSION_DENIED' || this.code === 'SCOPE_FORBIDDEN';
  }

  get isNotFound(): boolean {
    return this.code === 'NOT_FOUND';
  }

  /** A conflict the user can act on — a duplicate, a dependency, a stale edit. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

// ── the in-memory access token ──────────────────────────────
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ── the active scope headers ────────────────────────────────
/**
 * The scope the user chose in the switcher.
 *
 * Sent as headers rather than a query parameter so every call carries it
 * without each call site remembering. The server validates the ask against the
 * user's own assignments and falls back to their default if it does not match —
 * a header is a request, never a grant.
 */
let activeScope: { kind: string; id: string | null } | null = null;

export function setActiveScope(scope: { kind: string; id: string | null } | null): void {
  activeScope = scope;
}

export function getActiveScope(): { kind: string; id: string | null } | null {
  return activeScope;
}

// ── the logout hook ─────────────────────────────────────────
let onUnauthenticated: ((notice: string | null) => void) | null = null;

/**
 * Called when a refresh has failed and the session is genuinely over — with
 * what to tell the person when the server said why (`SESSION_ENDED`: signed
 * out from another device, by a new password, after going unused), else `null`.
 *
 * A hook rather than a direct store import, because the store imports the
 * client — and a cycle between the two is the kind of thing that works in
 * development and fails at build time in a different module order.
 */
export function setUnauthenticatedHandler(handler: ((notice: string | null) => void) | null): void {
  onUnauthenticated = handler;
}

let onConsentRequired: (() => void) | null = null;

/**
 * Called when the API refuses a request because the client owes a consent.
 *
 * The session is what says a consent is owed, and it was read before the
 * requirement came into force — so the handler re-reads it and the app's gate
 * takes over. Without this the person would keep meeting a refusal on every
 * screen until they reloaded.
 */
export function setConsentRequiredHandler(handler: (() => void) | null): void {
  onConsentRequired = handler;
}

function signedOut(): void {
  setAccessToken(null);
  onUnauthenticated?.(endedNotice);
}

// ── the single-flight refresh ───────────────────────────────
/** What a refresh came to: a new token, the server saying there is no session,
 *  or no answer at all — which is not the same as being signed out. */
type Refresh = 'refreshed' | 'rejected' | 'unreachable';

let refreshInFlight: Promise<Refresh> | null = null;

/**
 * Why the last refused refresh was refused, when the server said: the session
 * was ended on purpose or ran out unused. The last refresh's answer, not a
 * message to hand over once: the requests that shared that refresh each sign
 * out with it, and the last of them must not say less than the first. A
 * refusal without a reason — nobody was signed in on this browser — and every
 * refresh that worked leave it empty, so a cold load with no session shows the
 * sign-in with nothing to explain.
 */
let endedNotice: string | null = null;

async function noticeFrom(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as Partial<ErrorEnvelope>;
    return body.error?.code === 'SESSION_ENDED' ? body.error.message : null;
  } catch {
    return null;
  }
}

async function refreshOnce(): Promise<Refresh> {
  // Every caller that arrives while a refresh is running awaits the SAME
  // promise. Because refresh rotates the token, concurrent refreshes would
  // replay a spent one and log the user out — see the module docstring.
  if (refreshInFlight === null) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${apiBaseUrl()}/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            // This path does not go through `headersFor`, so the app header is
            // set explicitly. Omitting it would make a refresh resolve to the
            // narrower kind.
            ...(isConfigured() ? { 'X-Connect-App': appConfig().flavor } : {}),
          },
        });
        if (!response.ok) {
          endedNotice = await noticeFrom(response);
          return 'rejected';
        }
        endedNotice = null;
        const body = (await response.json()) as SuccessEnvelope<{ access_token: string }>;
        setAccessToken(body.data.access_token);
        return 'refreshed';
      } catch {
        // A network failure is not an expired session: the caller surfaces its
        // own error rather than pretending the user signed out.
        return 'unreachable';
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

// ── the request ─────────────────────────────────────────────
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialized as JSON. Omit for a GET. */
  body?: unknown;
  /**
   * Appended as a query string; `undefined`, `null` and empty values are
   * dropped. An array repeats its key once per entry (`?key=a&key=b`), which
   * is how FastAPI reads a `list[str]` query parameter.
   */
  params?: Record<
    string,
    string | number | boolean | undefined | null | readonly (string | number | boolean)[]
  >;
  signal?: AbortSignal;
  /** Skip the bearer token — for sign-in and the public pages. */
  anonymous?: boolean;
  /**
   * Sent as `Idempotency-Key`: one per intent, repeated on a retry of it, so the
   * server does the action once. See `useIdempotencyKey`.
   */
  idempotencyKey?: string;
  /**
   * Extra headers for this one call — a capability the caller declares, such as
   * `X-Connect-Two-Step`. Never the bearer token or the app's own headers,
   * which are set below and win.
   */
  headers?: Record<string, string>;
}

/** Exported for its test only; callers go through `request`. */
export function buildUrl(path: string, params?: RequestOptions['params']): string {
  const base = `${apiBaseUrl()}/v1${path.startsWith('/') ? path : `/${path}`}`;
  if (!params) return base;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    const entries = Array.isArray(value) ? value : [value];
    for (const entry of entries) {
      if (entry === '') continue;
      search.append(key, String(entry));
    }
  }
  const query = search.toString();
  return query ? `${base}?${query}` : base;
}

function headersFor(options: RequestOptions, accept = 'application/json'): Headers {
  const headers = new Headers({ ...options.headers, Accept: accept });
  // A form carries files: the browser writes its multipart boundary into the
  // Content-Type, so none is set here. Anything else is JSON.
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!options.anonymous && accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey);
  // Which application is calling. The production deployment serves both from
  // one host, so the Origin cannot distinguish them — this is the only signal
  // the API has, and it decides which door a sign-in uses rather than what the
  // caller may do. See `_serves_kind` on the API side.
  if (isConfigured()) headers.set('X-Connect-App', appConfig().flavor);
  // Recorded beside a consent as evidence of where it was given. Never used to
  // decide anything: a web session and a phone session are the same client.
  headers.set('X-Connect-Platform', 'web');
  if (activeScope) {
    headers.set('X-Scope-Kind', activeScope.kind);
    if (activeScope.id) headers.set('X-Scope-Id', activeScope.id);
  }
  return headers;
}

async function toApiError(response: Response): Promise<ApiError> {
  // A non-envelope body is possible — a proxy's own 502 page, a truncated
  // response. It must still become an ApiError, because every caller is written
  // to catch one and an unexpected shape here would surface as a render crash.
  let code = 'HTTP_ERROR';
  let message = 'Something went wrong. Try again.';
  let details: ApiErrorDetail[] = [];
  let requestId = response.headers.get('X-Request-ID') ?? '';
  try {
    const body = (await response.json()) as ErrorEnvelope;
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details ?? [];
      requestId = body.meta?.request_id ?? requestId;
    }
  } catch {
    if (response.status === 502 || response.status === 503 || response.status === 504) {
      code = 'SERVICE_UNAVAILABLE';
      message = 'The server is not responding. It may be restarting — try again in a moment.';
    }
  }
  return new ApiError({ status: response.status, code, message, details, requestId });
}

/**
 * A request under `/public/` is anonymous by definition: those are the API's
 * routes for people without an account. Asked otherwise, the client refreshed a
 * session first, found none, and answered "Sign in to continue." to a visitor
 * applying to the partner programme (2026-10-20) — one forgotten flag away on
 * every public page. Decided here, so no call can forget it.
 */
export function isPublicPath(path: string): boolean {
  return path === '/public' || path.startsWith('/public/');
}

/**
 * One authorized round trip, whatever the body is: the token, the single
 * refresh-and-retry on a 401, and the error envelope. JSON calls and file
 * downloads both come through here, so a download cannot be the one request
 * that forgets to refresh an expired token.
 */
async function authorizedFetch(
  path: string,
  requested: RequestOptions,
  accept: string,
  isRetry: boolean,
): Promise<Response> {
  const options =
    requested.anonymous === undefined && isPublicPath(path) ? { ...requested, anonymous: true } : requested;
  if (!options.anonymous && !isRetry && accessToken === null) {
    // No token in memory — a cold load, a new tab. Sent as it is, the request
    // could only be refused; refresh first. Having refreshed, it is the retry:
    // a 401 now means the session is over, not that it needs refreshing again.
    const refresh = await refreshOnce();
    if (refresh === 'rejected') {
      signedOut();
      // What the API itself answers a request without a session.
      throw new ApiError({ status: 401, code: 'UNAUTHENTICATED', message: 'Sign in to continue.' });
    }
    return authorizedFetch(path, options, accept, true);
  }
  const response = await fetch(buildUrl(path, options.params), {
    method: options.method ?? 'GET',
    headers: headersFor(options, accept),
    // Always: the refresh token is an httpOnly cookie, and a request without
    // credentials cannot refresh.
    credentials: 'include',
    body:
      options.body === undefined
        ? undefined
        : options.body instanceof FormData
          ? options.body
          : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 401 && !options.anonymous) {
    // One refresh and one retry, never a loop. A 401 after a refresh means the
    // session is genuinely over, and refreshing again would spin.
    if (!isRetry && (await refreshOnce()) === 'refreshed') {
      return authorizedFetch(path, options, accept, true);
    }
    signedOut();
    throw await toApiError(response);
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (error.code === 'CONSENT_REQUIRED') onConsentRequired?.();
    throw error;
  }
  return response;
}

async function send<T>(path: string, options: RequestOptions): Promise<SuccessEnvelope<T>> {
  const response = await authorizedFetch(path, options, 'application/json', false);
  if (response.status === 204) return { success: true, data: undefined as T, meta: { request_id: '', timestamp: '' } };

  return (await response.json()) as SuccessEnvelope<T>;
}

/** The unwrapped `data` of a successful call. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const envelope = await send<T>(path, options);
  return envelope.data;
}

/**
 * A server-side list call: rows plus the total from the pagination meta.
 *
 * Separate from {@link request} because the total lives in `meta`, not `data`,
 * and a table needs both. Every register read goes through here.
 */
export async function requestPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<OffsetPage<T>> {
  const envelope = await send<T[]>(path, options);
  const pagination = envelope.meta.pagination;
  const total =
    pagination && 'total' in pagination && typeof pagination.total === 'number'
      ? pagination.total
      : (envelope.data?.length ?? 0);
  return { rows: envelope.data ?? [], total };
}

/** A file the API sent: its bytes, and the name the server gave it. */
export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

/**
 * The file name from a `Content-Disposition` header, preferring the RFC 5987
 * `filename*` form. Exported for its test.
 */
export function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed escape: fall back to the plain form below.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
  return plain?.[1]?.trim() || null;
}

/** A non-JSON response — a CSV statement, an export — as a file. */
export async function requestFile(
  path: string,
  options: RequestOptions = {},
): Promise<DownloadedFile> {
  const response = await authorizedFetch(path, options, '*/*', false);
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition')),
  };
}

/** Hand a downloaded file to the browser's save flow. */
export function saveFile(file: DownloadedFile, fallbackName: string): void {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename ?? fallbackName;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next task: some browsers start the save asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  page: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    requestPage<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  /** Send a form with files — a photo into a chat — as multipart. */
  upload: <T>(path: string, form: FormData, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body: form }),
  /** Fetch a file — for a download, or to show a protected image. */
  file: (path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    requestFile(path, { ...options, method: 'GET' }),
  /** Fetch a file and save it, under the server's name or `fallbackName`. */
  download: async (
    path: string,
    fallbackName: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => saveFile(await requestFile(path, { ...options, method: 'GET' }), fallbackName),
};
